import { apiDelete, apiGet, apiPost, buildFormData } from '../api.utils'
import { IResponseCommon } from '../common.types'

export type ExtractionReference = {
    fileId: string
    documentId: string
    sha256: string
    page: number
    bbox: number[] | null
    rotationDegrees: number
}

export type ExtractionField = {
    name: string
    rawValue: string | null
    normalizedValue: string | null
    status: string
    confidence: number
    candidates: string[]
    evidence: ExtractionReference[]
    validation: { format: string; crossDocument: string }
}

export type ExtractionResult = {
    resultStatus: string
    documents: {
        documentId: string
        type: string
        pages: ExtractionReference[]
        fields: ExtractionField[]
        quality: { fileId: string; page: number; issue: string }[]
        relatedDocumentId: string | null
        relationStatus: string
    }[]
    persons: { personId: string; documentIds: string[]; linkStatus: string }[]
    conflicts: { code: string; fields: { documentId: string; field: string }[] }[]
    unresolved: { code: string; documentId: string | null; fileId: string | null; field: string | null }[]
}

export type ExtractionStage = {
    stageID: string
    status: string
    expiresAt: string
    parts: {
        index: number
        status: string
        error: string | null
        result: ExtractionResult | null
        files: { fileID: number; fileId: string; name: string; pages: number }[]
    }[]
}

type ExtractionResponse<T> = IResponseCommon & { data?: T }
const endpoint = '/listeners/documentExtraction'
const draftEndpoint = (draftID: string) => `/listeners/documentDrafts/${encodeURIComponent(draftID)}`
const extractionEndpoint = (scope: number | string) => typeof scope === 'number' ? endpoint : `${draftEndpoint(scope)}/extraction`
const extractionOptions = (scope: number | string, signal?: AbortSignal) => typeof scope === 'number' ? { params: { listenerID: scope }, signal } : { signal }

export const createListenerDraft = () =>
    apiPost<ExtractionResponse<{ draftID: string; expiresAt: number }>>('/listeners/documentDrafts')

export const uploadListenerDraft = (draftID: string, files: File[], signal?: AbortSignal) =>
    apiPost<ExtractionResponse<{ files: { fileID: number; name: string }[] }>>(`${draftEndpoint(draftID)}/files`, { signal, timeout: 10 * 60 * 1000 }, buildFormData({ docs: files }))

export const deleteListenerDraftFile = (draftID: string, fileID: number, signal?: AbortSignal) =>
    apiDelete<ExtractionResponse<{ deleted: boolean }>>(`${draftEndpoint(draftID)}/files/${fileID}`, { signal })

export const abandonListenerDraft = (draftID: string) =>
    apiDelete<ExtractionResponse<{ deleted: boolean }>>(draftEndpoint(draftID))

export const getDocumentExtractionConfig = (listenerID: number, signal?: AbortSignal) =>
    apiGet<ExtractionResponse<{ enabled: boolean }>>(`${endpoint}Config`, { params: { listenerID }, signal })

export const createDocumentExtraction = (listenerID: number | string, stageID: string, fileIDs: number[], signal?: AbortSignal) =>
    apiPost<ExtractionResponse<ExtractionStage>>(extractionEndpoint(listenerID), extractionOptions(listenerID, signal), { stageID, fileIDs })

export const getDocumentExtraction = (listenerID: number | string, stageID: string, signal?: AbortSignal) =>
    apiGet<ExtractionResponse<ExtractionStage>>(`${extractionEndpoint(listenerID)}/${encodeURIComponent(stageID)}`, extractionOptions(listenerID, signal))

export const cancelDocumentExtraction = (listenerID: number | string, stageID: string, signal?: AbortSignal) =>
    apiPost<ExtractionResponse<ExtractionStage>>(`${extractionEndpoint(listenerID)}/${encodeURIComponent(stageID)}/cancel`, extractionOptions(listenerID, signal))

export const deleteDocumentExtraction = (listenerID: number | string, stageID: string, signal?: AbortSignal) =>
    apiDelete<ExtractionResponse<{ deleted: boolean }>>(`${extractionEndpoint(listenerID)}/${encodeURIComponent(stageID)}`, extractionOptions(listenerID, signal))